import { DocTopic } from '../Constants/Documentation'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { Branding, getBranding } from '../API/BrandingAPI'
import { DoctopicWindow } from '../Types/Types'
import { getFeaturesConfigValue } from '../API/GenericConfigAPI'
import { ENABLE_GITBOOK_DOC } from '../../../ReactRouting/Constants/FeatureFlags'
import { APIElements } from '../../../NonReact/constants/ApiConstants'
import { SLASH } from '../Constants/Separators'

/**
 * Window with the dialog topic read by the classic F1 logic, plus the topic of the page currently shown.
 */
type HelpTopicWindow = DoctopicWindow & { pageTopicID: Definable<string> }

const $docTopicWindow: HelpTopicWindow = window as HelpTopicWindow

/**
 * Window as seen by help, including the topic captured by the classic JSP page.
 */
type HelpCaptureWindow = HelpTopicWindow & {
    m_pHelpCapture: {
        getTopicID: () => DocTopic
    }
}

/**
 * Help topics that only exist in GitBook, mapped to the classic help topic to open instead when GitBook help is off.
 * They open the Introduction, as they did before they had their own GitBook page.
 */
const CLASSIC_DOC_TOPIC_FALLBACK: Partial<Record<DocTopic, DocTopic>> = {
    [DocTopic.LICENCE_MANAGEMENT]: DocTopic.INTRODUCTION
}

/**
 * Opens help portal with topic provided as parameter.
 *
 * @param servletPath Servlet path.
 * @param topic Topic name / identifier.
 */
export const openHelpPortalByTopic = (servletPath: string, topic: string): void => {
    // eslint-disable-next-line
    com.Akixi.HelpPortal.OpenTopic(servletPath, topic)
}

export const openModalHelpPortal = (servletPath: string): void =>
    openHelpPortalByTopic(servletPath, getModalHelpTopicID())

/**
 * Clears the contextual doc topic ID from the window object that is used in old JSP logic that handles F1 key press.
 * When removed the previous logic will work and the modal window doc topic will be not considered.
 */
export const clearModalHelpTopic = (): void => {
    $docTopicWindow.modalTopicID = undefined
}

/**
 * Sets the contextual doc topic ID to the window object that is used in old JSP logic that handles F1 key press.
 * @param docTopicID
 */
export const setModalHelpTopicID = (docTopicID: string): void => {
    $docTopicWindow.modalTopicID = docTopicID
}

export const getModalHelpTopicID = (): DocTopic => $docTopicWindow.modalTopicID as DocTopic

/**
 * Sets the help topic of the page currently shown. Unlike the dialog topic set by {@link setModalHelpTopicID}, dialogs
 * do not clear it when they close, so help falls back to it once no dialog topic is set.
 *
 * @param docTopicID Topic name / identifier.
 */
export const setPageHelpTopicID = (docTopicID: string): void => {
    $docTopicWindow.pageTopicID = docTopicID
}

/**
 * Gets the help topic of the page currently shown.
 *
 * @returns Topic name / identifier.
 */
export const getPageHelpTopicID = (): DocTopic => $docTopicWindow.pageTopicID as DocTopic

/**
 * Works out the help topic to open: the open dialog's topic, else the current page's topic, else the topic captured
 * by the classic JSP page. When GitBook help is off, topics that only exist in GitBook are swapped for a classic
 * help topic so the classic help portal can find them.
 *
 * @param isGitBookDocEnabled Whether help is served from GitBook.
 * @returns Topic name / identifier.
 */
const getHelpTopicID = (isGitBookDocEnabled: boolean): string => {
    const windowRef: HelpCaptureWindow = $docTopicWindow as HelpCaptureWindow

    const pageTopicID: string = isStringNotEmpty(windowRef.pageTopicID)
        ? windowRef.pageTopicID
        : windowRef.m_pHelpCapture?.getTopicID?.()

    const topicID: string = isStringNotEmpty(windowRef.modalTopicID) ? windowRef.modalTopicID : pageTopicID

    return isGitBookDocEnabled ? topicID : CLASSIC_DOC_TOPIC_FALLBACK[topicID as DocTopic] ?? topicID
}

/**
 * Opens help portal with the topic of the open dialog or, failing that, of the current page.
 */
export const openHelp = (): void => {
    getBranding().then(async (branding: Nullable<Branding>): Promise<void> => {
        const isGitBookDocEnabled: Definable<boolean> = await getFeaturesConfigValue(ENABLE_GITBOOK_DOC)
        const servletPath: string = isGitBookDocEnabled
            ? '/CCS/API/v1/internal/doc/help'
            : `/CCS/App${branding?.URLMapping ?? SLASH + APIElements.AKIXI_2019}`

        openHelpPortalByTopic(servletPath, getHelpTopicID(isGitBookDocEnabled === true))
    })
}
